/**
 * Wefunder investor bar + modal.
 *
 * Renders in the header group so it appears site-wide. The bar's CTA button
 * toggles a modal (no navigation); the modal form subscribes the visitor to
 * Klaviyo before opening the Wefunder campaign in a new tab.
 */

(() => {
  const WEFUNDER_KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const WEFUNDER_KLAVIYO_REVISION = '2026-07-15';
  const KLAVIYO_MAX_ATTEMPTS = 3;
  const KLAVIYO_RETRY_BASE_MS = 1000;
  const KLAVIYO_RETRY_MAX_MS = 60000;
  const KLAVIYO_IDENTIFY_TIMEOUT_MS = 1000;
  const MINIMUM_SUBMIT_TIME_MS = 1500;
  const ATTRIBUTION_STORAGE_KEY = 'proof:wefunder_waitlist:attribution:v1';
  const DEFAULT_FORM_SOURCE = 'wefunder_waitlist';
  const URL_VALUE_MAX_LENGTH = 2048;
  const UTM_VALUE_MAX_LENGTH = 200;
  const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
  const SUBMISSION_ERROR_MESSAGE =
    'We couldn’t complete your request. Your information is still here—please try again.';

  const FOCUSABLE_SELECTOR = [
    'button:not([disabled])',
    '[href]',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  const compactProperties = (properties) =>
    Object.fromEntries(
      Object.entries(properties).filter(([, value]) => {
        if (value === undefined || value === null) return false;
        return typeof value !== 'string' || value.trim() !== '';
      })
    );

  const limitText = (value, maximumLength) => {
    if (typeof value !== 'string') return '';
    return value.trim().slice(0, maximumLength);
  };

  const sanitizeUrl = (value) => {
    if (!value) return '';

    try {
      const url = new URL(value, window.location.origin);
      Array.from(url.searchParams.keys()).forEach((key) => {
        if (key.toLowerCase() === 'srsltid') url.searchParams.delete(key);
      });
      return limitText(url.href, URL_VALUE_MAX_LENGTH);
    } catch (_error) {
      return '';
    }
  };

  const normalizeStoredAttribution = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return null;

    const attribution = {};
    UTM_KEYS.forEach((key) => {
      const normalized = limitText(value[key], UTM_VALUE_MAX_LENGTH);
      if (normalized) attribution[key] = normalized;
    });

    const initialLandingPage = sanitizeUrl(value.initial_landing_page);
    const initialReferrer = sanitizeUrl(value.initial_referrer);
    if (initialLandingPage) attribution.initial_landing_page = initialLandingPage;
    if (initialReferrer) attribution.initial_referrer = initialReferrer;

    return initialLandingPage ? attribution : null;
  };

  const getStoredAttribution = () => {
    try {
      return normalizeStoredAttribution(JSON.parse(window.sessionStorage.getItem(ATTRIBUTION_STORAGE_KEY)));
    } catch (_error) {
      return null;
    }
  };

  const captureInitialAttribution = () => {
    const storedAttribution = getStoredAttribution();
    if (storedAttribution) return storedAttribution;

    let landingUrl;
    try {
      landingUrl = new URL(window.location.href);
    } catch (_error) {
      return {};
    }

    const attribution = {};
    UTM_KEYS.forEach((key) => {
      const value = limitText(landingUrl.searchParams.get(key), UTM_VALUE_MAX_LENGTH);
      if (value) attribution[key] = value;
    });

    const initialLandingPage = sanitizeUrl(landingUrl.href);
    if (initialLandingPage) attribution.initial_landing_page = initialLandingPage;

    try {
      if (document.referrer) {
        const referrer = new URL(document.referrer, window.location.origin);
        if (referrer.origin !== window.location.origin) {
          const initialReferrer = sanitizeUrl(referrer.href);
          if (initialReferrer) attribution.initial_referrer = initialReferrer;
        }
      }
    } catch (_error) {
      // Invalid referrers are ignored; they should not block a legitimate signup.
    }

    try {
      window.sessionStorage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
    } catch (_error) {
      // Storage can be unavailable in privacy modes. This page-load copy still works.
    }

    return attribution;
  };

  // The header component is site-wide, so this runs on the first page in the session.
  // A duplicate script include reads the same value and never overwrites the landing data.
  const initialAttribution = captureInitialAttribution();

  if (customElements.get('wefunder-bar')) return;

  class WefunderBar extends HTMLElement {
    connectedCallback() {
      this.modal = this.querySelector('[data-wf-modal]');
      this.dialog = this.querySelector('[data-wf-dialog]');
      this.openButton = this.querySelector('[data-wf-open]');
      this.form = this.querySelector('[data-wf-form]');
      this.content = this.querySelector('[data-wf-content]');
      this.toast = this.querySelector('[data-wf-toast]');
      this.errorEl = this.querySelector('[data-wf-error]');
      this.statusEl = this.querySelector('[data-wf-status]');
      this.firstNameInput = this.querySelector('[data-wf-first-name]');
      this.lastNameInput = this.querySelector('[data-wf-last-name]');
      this.emailInput = this.querySelector('[data-wf-email]');
      this.phoneInput = this.querySelector('[data-wf-phone]');
      this.honeypotInput = this.querySelector('[data-wf-honeypot]');
      this.submitButton = this.querySelector('[data-wf-submit]');

      // The signup is used both inside the header modal and inline on the investor
      // opportunities page. The form is the shared requirement; modal controls are
      // optional for the inline version.
      if (!this.form) return;

      this.onKeydown = this.onKeydown.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.boundSubmit = this.onSubmit.bind(this);
      this.toastTimer = null;
      this.isSubmitting = false;
      this.formReadyAt = Date.now();
      this.submitButtonLabel = this.submitButton ? this.submitButton.textContent : '';

      if (this.openButton && this.modal) {
        this.openButton.addEventListener('click', () => this.open());
      }

      this.querySelectorAll('[data-wf-close], [data-wf-overlay]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      this.querySelectorAll('[data-wf-toast-close]').forEach((el) => {
        el.addEventListener('click', () => this.hideToast());
      });

      this.form.addEventListener('submit', this.boundSubmit);
    }

    disconnectedCallback() {
      // The theme editor tears sections down and rebuilds them; don't leave a
      // stray listener, a pending timer, or a locked <body> behind.
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.form?.removeEventListener('submit', this.boundSubmit);
      clearTimeout(this.toastTimer);
      document.body.classList.remove('overflow-hidden');
    }

    open(preserveReadyTime = false) {
      if (!this.modal || !this.openButton) return;

      this.modal.hidden = false;
      this.openButton.setAttribute('aria-expanded', 'true');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', this.onKeydown);
      if (!preserveReadyTime) this.formReadyAt = Date.now();

      (this.firstNameInput || this.dialog).focus();
    }

    close() {
      if (!this.modal || !this.openButton) return;

      this.modal.hidden = true;
      this.openButton.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', this.onKeydown);
      this.openButton.focus();
    }

    onKeydown(event) {
      if (!this.modal || !this.dialog || this.modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      // Trap focus inside the dialog while it is open.
      const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    showError(message, field) {
      if (this.errorEl) {
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }
      if (field) {
        field.closest('.wefunder-field')?.classList.add('wefunder-field--invalid');
        field.setAttribute('aria-invalid', 'true');
        if (this.errorEl?.id) field.setAttribute('aria-describedby', this.errorEl.id);
        field.focus();
      } else {
        this.errorEl?.focus();
      }
    }

    clearErrors() {
      if (this.errorEl) {
        this.errorEl.hidden = true;
        this.errorEl.textContent = '';
      }
      this.querySelectorAll('.wefunder-field--invalid').forEach((el) => {
        el.classList.remove('wefunder-field--invalid');
      });
      this.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
        field.removeAttribute('aria-invalid');
        if (field.getAttribute('aria-describedby') === this.errorEl?.id) {
          field.removeAttribute('aria-describedby');
        }
      });
    }

    setSubmitting(isSubmitting) {
      this.isSubmitting = isSubmitting;
      this.form.setAttribute('aria-busy', String(isSubmitting));

      if (this.submitButton) {
        this.submitButton.disabled = isSubmitting;
        this.submitButton.textContent = isSubmitting ? 'Submitting…' : this.submitButtonLabel;
      }

      if (this.statusEl) {
        this.statusEl.textContent = isSubmitting ? 'Submitting your request…' : '';
      }
    }

    isLikelyBot() {
      const honeypotFilled = Boolean(this.honeypotInput?.value.trim());
      const submittedTooQuickly = Date.now() - this.formReadyAt < MINIMUM_SUBMIT_TIME_MS;
      return honeypotFilled || submittedTooQuickly;
    }

    /**
     * Validation and window.open() remain synchronous inside the user gesture. The
     * child tab waits on a neutral progress page until Klaviyo confirms success.
     */
    async onSubmit(event) {
      event.preventDefault();
      if (this.isSubmitting) return;

      this.clearErrors();

      const firstName = this.firstNameInput.value.trim();
      const lastName = this.lastNameInput.value.trim();
      const email = this.emailInput.value.trim();
      const phoneRaw = this.phoneInput ? this.phoneInput.value.trim() : '';

      if (firstName === '') {
        this.showError('Please enter your first name.', this.firstNameInput);
        return;
      }

      if (lastName === '') {
        this.showError('Please enter your last name.', this.lastNameInput);
        return;
      }

      if (email === '' || !this.emailInput.checkValidity()) {
        this.showError('Please enter a valid email address.', this.emailInput);
        return;
      }

      // Phone is optional, but if they typed something unusable say so rather than
      // silently dropping it — they'd never know the number didn't reach us.
      const phone = WefunderBar.toE164(phoneRaw);
      if (phoneRaw !== '' && phone === null) {
        this.showError(
          'That phone number doesn’t look right. Clear it or use a 10-digit US number.',
          this.phoneInput
        );
        return;
      }

      if (this.isLikelyBot()) {
        this.showError(SUBMISSION_ERROR_MESSAGE);
        return;
      }

      const pendingTab = this.openPendingTab();
      this.setSubmitting(true);

      let subscribed = false;
      try {
        subscribed = await this.subscribeToKlaviyo({ firstName, lastName, email, phone });
      } catch (_error) {
        // Do not include request data or caught objects in logs: they may contain PII.
        console.error('[wefunder-bar] Klaviyo subscription failed unexpectedly');
      }

      if (!subscribed) {
        this.setSubmitting(false);
        this.closePendingTab(pendingTab);
        if (this.modal?.hidden) this.open(true);
        this.showError(SUBMISSION_ERROR_MESSAGE);
        return;
      }

      // Associate Klaviyo's onsite cookie/session with the profile so its native
      // attribution can be populated. This is supplementary to the confirmed
      // subscription and must never turn that success into a visible failure.
      await this.identifyWithKlaviyo({ firstName, lastName, email });
      this.setSubmitting(false);

      const profileProperties = this.buildProfileProperties();
      this.trackConversion(profileProperties);
      this.navigateToWefunder(pendingTab);

      if (this.modal) this.close();
      this.form.reset();
      this.formReadyAt = Date.now();
      this.clearErrors();
      this.showToast();
    }

    async identifyWithKlaviyo(
      { firstName, lastName, email },
      timeoutMs = KLAVIYO_IDENTIFY_TIMEOUT_MS
    ) {
      const identify = window.klaviyo?.identify;
      if (typeof identify !== 'function') return false;

      try {
        const identifyRequest = identify.call(window.klaviyo, {
          email,
          first_name: firstName,
          last_name: lastName,
        });

        return await new Promise((resolve) => {
          let finished = false;
          const finish = (identified) => {
            if (finished) return;
            finished = true;
            clearTimeout(timeout);
            resolve(identified);
          };
          const timeout = setTimeout(() => finish(false), timeoutMs);

          Promise.resolve(identifyRequest).then(
            () => finish(true),
            () => finish(false)
          );
        });
      } catch (_error) {
        return false;
      }
    }

    openPendingTab() {
      if (!this.dataset.wefunderUrl) return null;

      let pendingTab;
      try {
        pendingTab = window.open('', '_blank');
      } catch (_error) {
        return null;
      }
      if (!pendingTab) return null;

      try {
        pendingTab.opener = null;
        pendingTab.document.title = 'Opening Wefunder…';
        pendingTab.document.body.textContent = 'Completing your signup…';
        pendingTab.document.body.style.font = '16px/1.5 system-ui, sans-serif';
        pendingTab.document.body.style.padding = '2rem';
      } catch (_error) {
        // The tab is still a valid popup even if its placeholder cannot be styled.
      }

      return pendingTab;
    }

    closePendingTab(pendingTab) {
      try {
        if (pendingTab && !pendingTab.closed) pendingTab.close();
      } catch (_error) {
        // A user- or browser-closed child tab needs no further handling.
      }
    }

    navigateToWefunder(pendingTab) {
      const wefunderUrl = this.dataset.wefunderUrl;
      if (!wefunderUrl) return;

      let childNavigationFailed = false;
      try {
        if (pendingTab && !pendingTab.closed) {
          pendingTab.location.replace(wefunderUrl);
          return;
        }
      } catch (_error) {
        childNavigationFailed = true;
      }

      // If the synchronous popup was blocked, a same-tab navigation is the only
      // reliable way to preserve the promised Wefunder destination after success.
      if (!pendingTab || childNavigationFailed) {
        try {
          window.location.assign(wefunderUrl);
        } catch (_error) {
          console.error('[wefunder-bar] Wefunder navigation was blocked');
        }
      }
    }

    showToast() {
      if (!this.toast) return;

      clearTimeout(this.toastTimer);
      this.toast.hidden = false;

      if (document.visibilityState === 'visible') {
        // Next frame, so the element is laid out before the transition starts.
        requestAnimationFrame(() => this.toast.classList.add('wefunder-toast--visible'));
      } else {
        // rAF is paused in background tabs. Skip the entrance animation nobody is
        // there to see rather than leave the toast stranded at opacity 0.
        this.toast.classList.add('wefunder-toast--visible');
      }

      document.addEventListener('visibilitychange', this.onVisibilityChange);
      this.startToastTimer();
    }

    /**
     * Opening the Wefunder tab backgrounds this one. The countdown only runs while
     * this tab is visible, and restarts whenever the visitor comes back to it.
     */
    startToastTimer() {
      clearTimeout(this.toastTimer);
      if (document.visibilityState !== 'visible') return;

      const seconds = Number(this.toast.dataset.duration) || 5;
      this.toastTimer = setTimeout(() => this.hideToast(), seconds * 1000);
    }

    onVisibilityChange() {
      if (this.toast.hidden) return;

      if (document.visibilityState === 'visible') {
        this.startToastTimer();
      } else {
        clearTimeout(this.toastTimer);
      }
    }

    hideToast() {
      if (!this.toast) return;

      clearTimeout(this.toastTimer);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.toast.classList.remove('wefunder-toast--visible');

      const done = () => {
        this.toast.hidden = true;
        this.toast.removeEventListener('transitionend', done);
      };

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        done();
      } else {
        this.toast.addEventListener('transitionend', done);
        // transitionend doesn't fire if the element is already off-screen or the
        // transition is interrupted; don't strand it visible.
        setTimeout(done, 400);
      }
    }

    /**
     * Klaviyo requires E.164. Bare 10-digit input is assumed US (+1); this is a US
     * Reg CF raise. International numbers must be typed with their own + prefix.
     */
    static toE164(raw) {
      if (!raw) return null;

      const trimmed = raw.trim();
      const digits = trimmed.replace(/\D/g, '');

      if (trimmed.startsWith('+')) {
        return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
      }
      if (digits.length === 10) return `+1${digits}`;
      if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;

      return null;
    }

    buildProfileProperties() {
      const properties = compactProperties({
        form_source: this.dataset.formSource || DEFAULT_FORM_SOURCE,
        form_location: this.dataset.formLocation,
        ...initialAttribution,
        submission_page: sanitizeUrl(window.location.href),
      });

      return properties;
    }

    async subscribeToKlaviyo({ firstName, lastName, email, phone }) {
      const companyId = this.dataset.klaviyoCompanyId;
      const listId = this.dataset.klaviyoListId;

      if (!companyId || !listId) {
        console.error('[wefunder-bar] Klaviyo public company ID or list ID is missing');
        return false;
      }

      const profileAttributes = {
        email,
        first_name: firstName,
        last_name: lastName,
        subscriptions: {
          email: {
            marketing: { consent: 'SUBSCRIBED' },
          },
        },
        properties: this.buildProfileProperties(),
      };

      // Stored on the profile only. The subscriptions object explicitly contains
      // email and omits SMS, so this cannot create SMS marketing consent.
      if (phone) profileAttributes.phone_number = phone;

      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: profileAttributes,
              },
            },
          },
          relationships: {
            list: {
              data: { type: 'list', id: listId },
            },
          },
        },
      };

      return this.postWithRetry(
        `${WEFUNDER_KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`,
        payload
      );
    }

    /**
     * Retry only 429, 5xx, and network failures. Status-only logs intentionally omit
     * response bodies, request payloads, and thrown objects so PII cannot leak.
     */
    async postWithRetry(url, payload, attempts = KLAVIYO_MAX_ATTEMPTS) {
      for (let attempt = 1; attempt <= attempts; attempt += 1) {
        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: {
              Accept: 'application/vnd.api+json',
              'Content-Type': 'application/vnd.api+json',
              revision: WEFUNDER_KLAVIYO_REVISION,
            },
            body: JSON.stringify(payload),
            keepalive: true,
          });

          if (response.ok) return true;

          const retriable = response.status >= 500 || response.status === 429;
          if (!retriable) {
            console.error(`[wefunder-bar] Klaviyo subscription rejected (${response.status})`);
            return false;
          }

          if (attempt < attempts) {
            console.warn(
              `[wefunder-bar] Klaviyo subscription attempt ${attempt}/${attempts} returned ${response.status}; retrying`
            );
            await this.backoff(attempt, response.headers?.get('Retry-After'));
          }
        } catch (_error) {
          if (attempt < attempts) {
            console.warn(
              `[wefunder-bar] Klaviyo subscription attempt ${attempt}/${attempts} had a network error; retrying`
            );
            await this.backoff(attempt);
          }
        }
      }

      console.error(`[wefunder-bar] Klaviyo subscription failed after ${attempts} attempts`);
      return false;
    }

    // Exponential backoff with jitter. A valid Retry-After header takes precedence.
    backoff(attempt, retryAfter) {
      let delay;
      if (retryAfter) {
        const seconds = Number(retryAfter);
        const retryDate = Date.parse(retryAfter);
        delay = Number.isFinite(seconds) ? seconds * 1000 : retryDate - Date.now();
      }

      if (!Number.isFinite(delay) || delay < 0) {
        delay = KLAVIYO_RETRY_BASE_MS * 2 ** (attempt - 1) * (0.5 + Math.random());
      }

      return new Promise((resolve) => setTimeout(resolve, Math.min(delay, KLAVIYO_RETRY_MAX_MS)));
    }

    trackConversion(profileProperties) {
      const analyticsProperties = compactProperties({
        form_source: profileProperties.form_source,
        form_location: profileProperties.form_location,
        utm_source: profileProperties.utm_source,
        utm_medium: profileProperties.utm_medium,
        utm_campaign: profileProperties.utm_campaign,
        utm_content: profileProperties.utm_content,
        utm_term: profileProperties.utm_term,
      });

      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        try {
          window.dataLayer.push({ event: 'klaviyo_lead_submit', ...analyticsProperties });
        } catch (_error) {
          // Analytics must never block the confirmed signup or Wefunder navigation.
        }
      }

      if (typeof window.fbq === 'function') {
        try {
          window.fbq('track', 'Lead', analyticsProperties);
        } catch (_error) {
          // Meta failures are non-critical after Klaviyo has accepted the signup.
        }
      }
    }
  }

  customElements.define('wefunder-bar', WefunderBar);
})();
